import {type OutputLine} from './transcript';

/**
 * Controls how whitespace is normalized before console output comparison.
 */
export type WhitespaceMode = 'exact' | 'trim' | 'collapse' | 'strip-all';

/**
 * Controls how letter casing is normalized before console output comparison.
 */
export type CasingMode = 'exact' | 'ignore';

/**
 * Comparison settings for console output.
 */
export interface ConsoleOutputComparisonOptions {
  /** How whitespace should be normalized before comparison. */
  whitespaceMode: WhitespaceMode;
  /** How letter casing should be normalized before comparison. */
  casingMode: CasingMode;
}

/**
 * Maps the legacy "ignore whitespace" flag to an explicit whitespace mode.
 */
export function getWhitespaceModeFromLegacyFlag(
  shouldIgnoreWhitespace: boolean,
): WhitespaceMode {
  return shouldIgnoreWhitespace ? 'strip-all' : 'exact';
}

/**
 * Maps the legacy "ignore casing" flag to an explicit casing mode.
 */
export function getCasingModeFromLegacyFlag(
  shouldIgnoreCasing: boolean,
): CasingMode {
  return shouldIgnoreCasing ? 'ignore' : 'exact';
}

/**
 * Normalizes a single console output line according to the given modes.
 */
export function normalizeOutputLine(
  line: OutputLine,
  whitespaceMode: WhitespaceMode,
  casingMode: CasingMode,
): string {
  let result: string;
  switch (whitespaceMode) {
    case 'exact':
      result = line;
      break;
    case 'trim':
      result = line.trim();
      break;
    case 'collapse':
      result = line.trim().replace(/\s+/g, ' ');
      break;
    case 'strip-all':
      result = line.replace(/\s+/g, '');
      break;
  }
  return casingMode === 'ignore' ? result.toLowerCase() : result;
}

/**
 * Returns whether a line participates in output comparison for the given mode.
 */
export function shouldIncludeOutputLineInComparison(
  line: OutputLine,
  whitespaceMode: WhitespaceMode,
): boolean {
  if (whitespaceMode === 'exact') {
    return true;
  }
  // Casing is irrelevant to whether a line is blank, so we pass 'exact'
  // This will be obseleted in the next PR of the stack.
  return normalizeOutputLine(line, whitespaceMode, 'exact').length > 0;
}

/**
 * Produces the normalized line list used by the autograder's equality check.
 */
export function normalizeOutputLinesForComparison(
  lines: OutputLine[],
  {whitespaceMode, casingMode}: ConsoleOutputComparisonOptions,
): string[] {
  return lines
    .filter(line => shouldIncludeOutputLineInComparison(line, whitespaceMode))
    .map(line => normalizeOutputLine(line, whitespaceMode, casingMode));
}

/**
 * Compares two console output transcripts using shared normalization rules.
 */
export function areConsoleOutputsEqual(
  testOutput: OutputLine[],
  userOutput: OutputLine[],
  options: ConsoleOutputComparisonOptions,
): boolean {
  if (testOutput === userOutput) {
    return true;
  }
  if (testOutput == null || userOutput == null) {
    return false;
  }

  const formattedTestOutput = normalizeOutputLinesForComparison(
    testOutput,
    options,
  );
  const formattedUserOutput = normalizeOutputLinesForComparison(
    userOutput,
    options,
  );

  if (formattedTestOutput.length !== formattedUserOutput.length) {
    return false;
  }

  for (let i = 0; i < formattedTestOutput.length; i++) {
    if (formattedTestOutput[i] !== formattedUserOutput[i]) {
      return false;
    }
  }
  return true;
}
